'use client';
// import { scan } from "react-scan";
import Avo from '@susu/custom-made/Avo';
import type { Item } from '@susu/custom-made/components/navigation/navigation';
import Navigation from '@susu/custom-made/components/navigation/navigation';
import { useMissingValues } from '@susu/custom-made/hooks/useMissingValues';
import { ThemeContext } from '@susu/custom-made/providers';
import { actions as globalActions } from '@susu/custom-made/providers/reducers/global';
import { actions as optionActions } from '@susu/custom-made/providers/reducers/option';
import { loadAnalytics } from '@susu/custom-made/segmentAnalytics';
import { SegmentDestination } from '@susu/custom-made/segmentDestination';
import {
  applyBusinessRulesForFabricSelection,
  isOptionHiddenByRule,
  isOptionValueHiddenByRule,
} from '@susu/custom-made/utils/businessRules';
import type { ClientType, ConfiguratorOriginsType } from '@susu/custom-made/utils/constants';
import {
  Clients,
  ConfiguratorOrigin,
  hiddenValuesStorageName,
  Level,
  stateExpiryStorageName,
  stateStorageName,
  storageTTL,
} from '@susu/custom-made/utils/constants';
import type {
  Fabric,
  BusinessRules,
  DefaultValues,
  FabricFilter,
  FabricGroup,
  LayerDefinitions,
  Option,
  OptionConfigurationType,
  StylePresets,
  typeEnvironments,
  typeFabricList,
  typeOptionValue,
  typeTranslations,
} from '@susu/custom-made/utils/interfaces';
import type { DataDogConfig } from '@susu/custom-made/utils/monitoring/dataDog';
import { initializeDataDog } from '@susu/custom-made/utils/monitoring/dataDog';
import {
  changeSection,
  getConfigurationTypeBySection,
  getProductSections,
  Section,
} from '@susu/custom-made/utils/navigation';
import {
  ConfigurationTypes,
  ProductType,
  getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId,
} from '@susu/custom-made/utils/productDefinition';
import {
  calculateFabricPrice,
  getAvoEventParams,
  getAvoEventParamsForPageLoad,
  getAvoEventParamsForProductViewed,
  getCMDatadogLogger,
  getConfigurationTypeText,
  isAdditonalProduct,
  isExtraConfigurationSection,
  isFalsy,
  isServerRender,
  processPreFilterFormat,
  shouldAddAdditonalProduct,
  validatePreFilters,
} from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import { useSearchParams } from 'next/navigation';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ButtonType } from '../button/button';
import { FabricPanel } from '../fabricPanel/fabricPanel';
import MissingOptionsDetails from '../missingOptionDetails/missingOptionsDetails';
import Modal from '../modal/modal';
import { OnBoardingContainer } from '../onBoarding/onBoardingContainer/onBoardingContainer';
import { OptionPanel } from '../optionPanel/optionPanel';
import Panel, { PanelSize } from '../panel/panel';
import PreviewHeader from '../previewHeader/previewHeader';
import styles from './sectionContainer.module.scss';
import useFirstRender from './useFirstRender';
import { PreviewProps } from '../preview/preview';
import dynamic from 'next/dynamic';
const Summary = dynamic(() => import('../summary/summary'), {ssr: false});

export const SectionContainer = ({
  fabricList,
  fabricGroups,
  fabricFilters,
  fabricPreFilters,
  stylePresets,
  translations,
  businessRules,
  options,
  optionValues,
  defaultValues,
  layerDefinitions,
  datadogClientToken,
  datadogApplicationId,
  countryCode,
  client,
  product,
  origin,
  defaultLayers,
  defaultSection,
  isInPreloadFlow,
  isCustomizeFlow,
}: {
  fabricList: typeFabricList;
  fabricGroups: FabricGroup[];
  fabricFilters: FabricFilter[];
  fabricPreFilters: string;
  stylePresets: StylePresets;
  translations: typeTranslations;
  businessRules: BusinessRules;
  options: OptionConfigurationType;
  optionValues: typeOptionValue;
  defaultValues: DefaultValues;
  layerDefinitions: LayerDefinitions;
  datadogClientToken: string;
  datadogApplicationId: string;
  countryCode: string;
  client: ClientType;
  product: ProductType;
  origin: ConfiguratorOriginsType;
  defaultLayers: Map<string, PreviewProps>;
  defaultSection: Section;
  isInPreloadFlow: boolean;
  isCustomizeFlow: boolean;
}) => {
  const { state, dispatch } = useContext(ThemeContext);
  const searchParams = useSearchParams();
  const currentSection = useMemo(() => state.section ?? defaultSection, [state.section]);
  const isFirstRender = useFirstRender();
  const [preFilters, setPreFilters] = useState<Record<string, string[]> | null>(null);
  const {
    getSectionsWithMissingValues,
    areAllConfigurationMissingValuesEmpty,
    closeMissingOptionValuesPanel,
    openMissingOptionValuesPanel,
    closeMissingFabricModal,
  } = useMissingValues();

  const setSection = useCallback(
    (section: string | null, level: string | null) => {
      const toSection = section ?? Section.Fabric;
      const toLevel = level ?? Level.Group;
      changeSection(toSection, toLevel, dispatch);
    },
    [dispatch],
  );

  const matchingProductPresets = useCallback(() => {
    if (!state.section) {
      return [];
    }
    // To handle extra configurations which have different configuration type id but using definitions from base product
    const configurationTypeId = getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(
      +getConfigurationTypeBySection(state.section),
    );
    if (!configurationTypeId || !stylePresets.configurationTypes[configurationTypeId]) {
      return [];
    }

    return stylePresets.configurationTypes[configurationTypeId].styles;
  }, [state.section, stylePresets]);

  const updateSection = useCallback(
    (item: Item | Option) => {
      const tmpItem = item as Item;

      setSection(tmpItem!.id, Level.Group);
      state.client === Clients.onLine && Avo.customMadeCategoryChanged(getAvoEventParams(state));
    },
    [setSection, client],
  );

  const navigation = useMemo<JSX.Element | undefined>(() => {
    if (isExtraConfigurationSection(currentSection as Section)) {
      return undefined;
    }

    return (
      <nav>
        <Navigation
          items={[
            ...getProductSections(product).map(section => ({
              text: section.text,
              id: section.slug,
              isActive: currentSection === section.slug,
              hasMissingValues: getSectionsWithMissingValues().includes(section.slug),
            })),
          ]}
          testId="section-nav"
          onItemClick={item => updateSection(item)}
        />
      </nav>
    );
  }, [product, getSectionsWithMissingValues, currentSection, updateSection]);

  const extraProductHeader = useMemo<JSX.Element | null>(() => {
    if (
      isExtraConfigurationSection(currentSection as Section) &&
      state.level === Level.Group &&
      !state.isBaseStyleSelection
    ) {
      return (
        <div className={styles.extraProductHeader} data-test-id="extra-product-section-header">
          {getConfigurationTypeText(getConfigurationTypeBySection(currentSection))}
        </div>
      );
    }
    return null;
  }, [currentSection, state.level, state.isBaseStyleSelection]);

  const showPreview = (tmpSection: string) => {
    return (
      tmpSection === Section.Fabric ||
      ((!state.isBaseStyleSelection || shouldAddAdditonalProduct(state)) && tmpSection !== Section.Summary)
    );
  };

  const isLocalStorageActive = useCallback(() => {
    if (isServerRender() || client === Clients.inStore) return false;

    const localStateExpiry = localStorage.getItem(stateExpiryStorageName);
    if (
      localStateExpiry &&
      parseInt(localStateExpiry) >= new Date().getTime() &&
      !fabricPreFilters &&
      !isCustomizeFlow &&
      !isInPreloadFlow
    ) {
      const localState = localStorage.getItem(stateStorageName)
        ? JSON.parse(localStorage.getItem(stateStorageName) as string)
        : null;

      return localState && product === localState.product && countryCode === localState.countryCode;
    } else {
      return false;
    }
  }, [product, countryCode, fabricPreFilters, isCustomizeFlow, isInPreloadFlow]);

  const applyFabricBusinessRules = useCallback(
    (selectedFabric: Fabric, prevFabric?: Fabric) => {
      if (selectedFabric && defaultValues && optionValues && options && businessRules && state.product && dispatch) {
        const previousSelectedOptions = structuredClone(state.selectedOptions);
        const previousHiddenOptionsAndValues = structuredClone(state.hiddenOptionsAndValues);

        // Loop business rules on fabric selection for option values and monogram
        let selectedOptions = structuredClone(state.selectedOptions);

        const updatedValues = {
          ...applyBusinessRulesForFabricSelection(
            selectedOptions,
            businessRules,
            structuredClone(state.hiddenOptionsAndValues),
            optionValues,
            selectedFabric.id,
            options,
            defaultValues,
            state.product,
            state.isManualSelectionDone,
            state.selectedBaseStyle,
            state.growthbookFeatures['show-logs'],
            prevFabric?.id,
            isInPreloadFlow,
          ),
        };

        dispatch({
          type: optionActions.SET_HIDDEN_OPTIONS_AND_VALUES,
          payload: updatedValues.hiddenOptionsAndValues,
        });

        dispatch({
          type: optionActions.SELECT_OPTION,
          payload: updatedValues.selectedOptions,
        });

        getCMDatadogLogger()?.info('Custom made - Fabric Clicked' ,
          {customData: {
            previousSelectedFabric: prevFabric,
            previousSelectedOptions,
            previousHiddenOptionsAndValues,
            selectedFabric,
            selectedOptions: updatedValues.selectedOptions,
            hiddenOptionsAndValues: updatedValues.hiddenOptionsAndValues,
          }}
        );
      }
    },
    [
      defaultValues,
      optionValues,
      businessRules,
      dispatch,
      options,
      state.product,
      state.isManualSelectionDone,
      state.selectedBaseStyle,
      state.selectedOptions,
      state.hiddenOptionsAndValues,
      isInPreloadFlow,
    ],
  );

  const popStateEvent = useCallback(
    (event: any) => {
      const params = event.target.location.search.replace('?', '').split('&');

      if (params.length) {
        let section = null;
        let level = null;
        params.forEach((param: string) => {
          if (param.split('=')[0] === 'section') {
            section = param.split('=')[1];
          }
          if (param.split('=')[0] === 'level') {
            level = param.split('=')[1];
          }
        });

        if (section || level) {
          setSection(section, level);
        }
      }
    },
    [setSection],
  );

  const initializeDataDogSetup = () => {
    const dataDogConfiguration: DataDogConfig = {
      applicationId: datadogApplicationId,
      clientToken: datadogClientToken,
      env: (process.env.NEXT_PUBLIC_CM_env as typeEnvironments) ?? 'dev',
    };
    initializeDataDog(dataDogConfiguration);
  };

  // Token injection on initial load
  const messageHandler = (e: MessageEvent) => {
    if (e.data.type !== 'configurators:tokenInjection') return;

    const token = e.data.payload;

    if (!isFalsy(token) && token !== state.token)
      dispatch({
        type: globalActions.SET_TOKEN,
        payload: e.data.payload,
      });
  };
  const injectToken = () => {
    if (client === Clients.inStore && isFalsy(state.token)) {
      // eslint-disable-next-line sonar/post-message
      window.addEventListener('message', messageHandler);

      // eslint-disable-next-line sonar/post-message
      window.parent.postMessage(
        {
          type: 'configurators:loaded',
        },
        '*',
      );
    }
  };

  const initializeAvo = () => {
    if (client === Clients.onLine) {
      Avo.initAvo(
        {
          env: process.env.NEXT_PUBLIC_CM_env === 'prd' ? Avo.AvoEnv.Prod : Avo.AvoEnv.Dev,
          webDebugger: false,
        },
        null,
        SegmentDestination,
      );

      loadAnalytics();
    }
  };

  const populateStateFromStorage = () => {
    if (optionValues && fabricList && fabricGroups && isLocalStorageActive()) {
      let localState = JSON.parse(localStorage.getItem(stateStorageName) as string);

      if (searchParams) {
        const currentSection = searchParams.get('section');
        const currentLevel = searchParams.get('level');
        if (currentSection) {
          localState = { ...localState, section: currentSection };
        }

        if (
          currentLevel &&
          ((currentSection === Section.Fabric && state.selectedFabricGroup) || state.selectedOptionGroup)
        ) {
          localState = { ...localState, level: currentLevel };
        }
      }

      dispatch({
        type: globalActions.POPULATE_STATE,
        payload: localState,
      });

      if (localStorage.getItem(hiddenValuesStorageName)) {
        const hiddenOptionsAndValues = JSON.parse(localStorage.getItem(hiddenValuesStorageName) as string);
        dispatch({
          type: optionActions.SET_HIDDEN_OPTIONS_AND_VALUES,
          payload: hiddenOptionsAndValues,
        });
      }

      setSection(localState.section, localState.level);
    }
  };

  // To set default section and level in URL on initial load
  useEffect(() => {
    const setDefaultSection = () => {
      if (!isFirstRender) {
        return;
      }

      if (searchParams) {
        setSection(defaultSection, state.level);
      }
    };

    setDefaultSection();
  }, [searchParams, isFirstRender]);

  useEffect(() => {
    // Enable this to run react scan
    // scan({log: false});

    // To set the token
    injectToken();

    // To set and cleanup pop state event listener
    window.addEventListener('popstate', popStateEvent, false);

    // To initialize datadog
    initializeDataDogSetup();

    // To initialize Avo
    initializeAvo();

    // To populate state from localstorage
    populateStateFromStorage();

    getCMDatadogLogger()?.info('Custom made - Initial Load' ,
      {customData: {
        selectedFabric: state.selectedFabric,
        selectedOptions: state.selectedOptions,
        hiddenOptionsAndValues: state.hiddenOptionsAndValues,
      }}
    );

    return () => {
      window.removeEventListener('popstate', popStateEvent, false);
      window.removeEventListener('message', messageHandler);
    };
  }, []);

  useEffect(() => {
    if (fabricPreFilters) {
      const preFilters = processPreFilterFormat(fabricPreFilters);
      const validatedPreFilters = validatePreFilters(preFilters, fabricFilters);
      if (Object.keys(validatedPreFilters).length) {
        setPreFilters(validatedPreFilters);
      }
    }
  }, [fabricPreFilters, fabricFilters]);

  // To show preset selection depending on the section and flow
  useEffect(() => {
    if (state.section) {
      const configurationTypeId = getConfigurationTypeBySection(state.section);
      let isBaseStyleSelectedForConfiguration = state.selectedBaseStyle[configurationTypeId];

      if (!isBaseStyleSelectedForConfiguration && isLocalStorageActive() && isFirstRender) {
        let localState = JSON.parse(localStorage.getItem(stateStorageName) as string);
        isBaseStyleSelectedForConfiguration = localState?.selectedBaseStyle[configurationTypeId];
      }

      if (
        state.section !== Section.Fabric &&
        state.section !== Section.Summary &&
        !isInPreloadFlow &&
        !isBaseStyleSelectedForConfiguration &&
        matchingProductPresets().length > 0
      ) {
        dispatch({
          type: optionActions.CHANGE_BASE_STYLE_STATUS,
          payload: true,
        });
      }
    }
  }, [state.section, state.selectedBaseStyle, dispatch, matchingProductPresets, isInPreloadFlow]);

  // To calculate total price on any selection change
  useEffect(() => {
    if (
      state.selectedFabric &&
      state.selectedOptions &&
      state.hiddenOptionsAndValues &&
      optionValues &&
      state.product
    ) {
      let totalPrice = calculateFabricPrice(state.product, state.selectedFabric.prices?.configurationTypes);

      Object.entries(state.selectedOptions).forEach(([configurationTypeId, configurationSelectedOptions]) => {
        Object.entries(configurationSelectedOptions).forEach(([optionId, optionValueId]) => {
          const addtionalPrice = optionValues[optionValueId]?.additionalPrice?.amount;
          if(addtionalPrice) {
            const isOptionValueNotInHiddenList = !isOptionValueHiddenByRule(
              optionValueId,
              state.hiddenOptionsAndValues,
              configurationTypeId as ConfigurationTypes,
            );
            const isOptionNotInHiddenList = !isOptionHiddenByRule(
              +optionId,
              state.hiddenOptionsAndValues,
              configurationTypeId as ConfigurationTypes,
            );

            const priceShouldApply = !isAdditonalProduct(product, configurationTypeId as ConfigurationTypes) || 
              (isAdditonalProduct(product, configurationTypeId as ConfigurationTypes) &&
                state.isAdditionalProductAdded[configurationTypeId]);

            if (
              isOptionValueNotInHiddenList &&
              isOptionNotInHiddenList &&
              priceShouldApply
            ) {
              totalPrice += addtionalPrice;
            }
          }
        });
      });

      Object.keys(state.isAdditionalProductAdded).forEach(configurationTypeId => {
        if (state.isAdditionalProductAdded[configurationTypeId]) {
          // To handle extra configurations which have different configuration type id but using definitions from base product
          const baseConfigurationTypeId =
            getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationTypeId);
          const additionalPrice = state.selectedFabric?.prices?.configurationTypes[+baseConfigurationTypeId];
          if (additionalPrice) {
            totalPrice += additionalPrice;
          }
        }
      });

      dispatch({
        type: globalActions.UPDATE_PRICE,
        payload: totalPrice,
      });
    }
  }, [
    state.product,
    state.selectedFabric,
    state.selectedOptions,
    state.isAdditionalProductAdded,
    optionValues,
    state.hiddenOptionsAndValues,
    dispatch,
  ]);

  // Clear monogram in case monogram color is hidden
  useEffect(() => {
    if (state.hiddenOptionsAndValues && state.selectedMonogram) {
      Object.values(state.selectedMonogram).forEach(monogram => {
        if (state.hiddenOptionsAndValues.monogramColors.indexOf(monogram.colorId) >= 0) {
          dispatch({
            type: optionActions.CLEAR_MONOGRAMS,
          });
        }
      });
    }
  }, [dispatch, state.hiddenOptionsAndValues, state.selectedMonogram]);

  // To add Avo page load event on section change & trigger localstorage on Summary
  useEffect(() => {
    if (state.section && client === Clients.onLine && state.product) {
      // Load Analytics takes time to load, so we need to wait for it to load before sending events
      setTimeout(() => {
        Avo.pageLoaded(getAvoEventParamsForPageLoad(state));
      }, 1000);
      if (state.section !== Section.Fabric && state.section !== Section.Summary) {
        setTimeout(() => {
          Avo.productViewedCm(getAvoEventParamsForProductViewed(state));
        }, 1000);
      }
    }

    if (state.section === Section.Summary && !isInPreloadFlow) {
      localStorage.setItem(stateStorageName, JSON.stringify(state));
      localStorage.setItem(hiddenValuesStorageName, JSON.stringify(state.hiddenOptionsAndValues));
      localStorage.setItem(stateExpiryStorageName, (new Date().getTime() + storageTTL).toString());
    }
  }, [state.section, client, state.product, isInPreloadFlow]);

  return (
    <>
      <Panel
        isActive={state.showMissingOptionsPanel}
        closeClick={closeMissingOptionValuesPanel}
        panelSize={PanelSize.Content}
        hasCloseBtn={true}
        withOverlay={true}
      >
        <MissingOptionsDetails
          options={options}
          optionValues={optionValues}
          missingOptionValuesPerConfig={state.missingOptionValuesPerConfig}
          missingMonogramValuesPerConfig={state.missingMonogramValuesPerConfig}
          missingOptionsPerConfig={state.missingOptionsPerConfig}
          isSummaryValidationFlow={state.isSummaryValidationTriggered}
        />
      </Panel>
      {!isCustomizeFlow &&
        state.product === 'Suit' &&
        state.client === 'onLine' &&
        origin === ConfiguratorOrigin.Normal && <OnBoardingContainer />}
      {currentSection && (
        <div
          className={cn(styles.layout, {
            [styles.noPreview]: !showPreview(currentSection),
          })}
        >
          {showPreview(currentSection) && (
            <div className={styles.previewContainer}>
              <div className={styles.header}>
                <PreviewHeader
                  translations={translations}
                  layerDefinitions={layerDefinitions}
                  optionValues={optionValues}
                  product={product}
                  hasMissingSelection={!areAllConfigurationMissingValuesEmpty()}
                  toastBtnClickHandler={openMissingOptionValuesPanel}
                  stylePresets={stylePresets}
                  isFirstRender={isFirstRender && !isLocalStorageActive()}
                  defaultLayers={defaultLayers}
                  isExtraProduct={isExtraConfigurationSection(currentSection as Section)}
                />
              </div>
            </div>
          )}
          <div className={styles.section}>
            {currentSection !== Section.Summary && (
              <section className={styles.sectionParentContainer}>
                <div
                  className={cn(styles.sectionContainer, {
                    [styles.inStore]: state.client === Clients.inStore,
                  })}
                >
                  {currentSection === Section.Fabric && (
                    <FabricPanel
                      fabricGroups={fabricGroups}
                      fabricList={fabricList}
                      fabricFilters={fabricFilters}
                      fabricPreFilters={preFilters}
                      isFirstRender={isFirstRender && !isLocalStorageActive()}
                      navigation={navigation}
                      applyFabricBusinessRules={applyFabricBusinessRules}
                      isCustomizeFlow={isCustomizeFlow}
                      origin={origin}
                    />
                  )}
                  {currentSection !== Section.Fabric && (
                    <OptionPanel
                      options={
                        options[
                          // To handle extra configurations which have different configuration type id but using definitions from base product
                          getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(
                            +getConfigurationTypeBySection(currentSection),
                          )
                        ]?.items
                      }
                      optionValues={optionValues}
                      extraProductHeader={extraProductHeader}
                      navigation={navigation}
                      stylePresets={matchingProductPresets()}
                      businessRules={businessRules}
                      layerDefinitions={layerDefinitions}
                      defaultValues={defaultValues}
                      isInPreloadFlow={isInPreloadFlow}
                    />
                  )}
                </div>
              </section>
            )}
            {currentSection === Section.Summary && (
              <Summary
                layerDefinitions={layerDefinitions}
                optionValues={optionValues}
                options={options}
                fabricList={fabricList}
                stylePresets={stylePresets}
                isInPreloadFlow={isInPreloadFlow}
              />
            )}
          </div>
          {state.showMissingFabricModal && (
            <div>
              <Modal
                isOpen={true}
                title="Fabric out of stock"
                description="Unfortunately, the selected fabric is unavailable. Changing fabric might alter your design, so please review it afterwards."
                firstButtonText="Change fabric"
                firstButtonType={ButtonType.Primary}
                isFullSize={true}
                onFirstButtonClick={closeMissingFabricModal}
                testId="section-oos-fabric-modal"
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};